import { useState, useEffect, useRef } from "react";
import wordmarkLockup from "./sr-wordmark-lockup.png";
import { C, FONT, Reveal } from "./ui/theme";
import { PillButton, TextLinkButton, NavLinkButton, marketingButtonStyles } from "./ui/Button";
import { plans } from "./PlanSelectionPage";
import { LEGAL_ENTITY, CONTACT_EMAIL } from "./LegalPages";
import BookDemoModal from "./BookDemoModal";
import heroPhoto from "./assets/hero-team-success.jpg";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Short, plain-language positioning line under each plan name in the
// Pricing section — purely presentational, kept local to this file (not
// part of the shared `plans` data) so it can't affect the real in-app
// plan-selection page or any billing logic.
const PRICING_POSITIONING = {
  trial: "Try SDR ReachOut",
  starter: "Individual & small teams",
  growth: "Growing SDR teams",
  scale: "Larger revenue orgs",
};

function PricingCheckIcon({ tone }) {
  return (
    <span aria-hidden style={{ width: 18, height: 18, borderRadius: "50%", background: tone + "17", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
        <path d="M2.2 6.3L4.6 8.7L9.8 3.2" stroke={tone} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// Hero mockup card: the sidebar auto-cycles through these six sections
// (one at a time, looping) and the KPI tiles + heading swap to match
// whichever section is currently highlighted — same idea as the
// Dashboard's own stat tiles, just one data set per nav item so the
// mockup demonstrates that every section has real reporting, not just
// the Dashboard. Numbers are illustrative, same as the rest of this mockup.
const MOCKUP_NAV = [
  {
    label: "Dashboard", heading: "Good morning, John",
    kpis: [["Total Leads", "28"], ["Emails Sent", "4,802"], ["Open Rate", "79%"]],
    feedLabel: "Recent replies",
    feed: [["Sarah Chen", "Interested — let's connect!"], ["Mark Davis", "Can you send more info?"]],
  },
  {
    label: "Campaigns", heading: "Campaign performance",
    kpis: [["Active Campaigns", "12"], ["Leads Contacted", "3,150"], ["Reply Rate", "24%"]],
    feedLabel: "Top campaigns",
    feed: [["Q3 Outbound Blast", "42% open rate"], ["Enterprise ABM", "18 replies this week"]],
  },
  {
    label: "Contacts", heading: "Your contacts",
    kpis: [["Total Contacts", "8,420"], ["New This Week", "310"], ["Verified Emails", "96%"]],
    feedLabel: "New contacts",
    feed: [["Priya Nair", "Added from LinkedIn import"], ["Tom Becker", "Verified — VP Sales"]],
  },
  {
    label: "Sequences", heading: "Active sequences",
    kpis: [["Running Sequences", "9"], ["Enrolled Contacts", "2,640"], ["Completion Rate", "68%"]],
    feedLabel: "Sequence activity",
    feed: [["Cold Outbound v2", "312 contacts enrolled"], ["Follow-up Nudge", "68% completion"]],
  },
  {
    label: "LinkedIn", heading: "LinkedIn outreach",
    kpis: [["Connections Sent", "1,920"], ["Accepted Rate", "61%"], ["InMail Replies", "148"]],
    feedLabel: "LinkedIn activity",
    feed: [["Alicia Gomez", "Connection accepted"], ["Ravi Shah", "Replied to InMail"]],
  },
  {
    label: "Replies", heading: "Recent replies",
    kpis: [["Total Replies", "512"], ["Positive Replies", "187"], ["Meetings Booked", "64"]],
    feedLabel: "Recent replies",
    feed: [["Jordan Lee", "Yes, send over pricing"], ["Nina Patel", "Interested — let's connect!"]],
  },
];

// Hero-only marketing page. Visual system: light, high-contrast, generous
// whitespace, restrained pill buttons, controlled type scale — Apple's
// design *principles* (hierarchy, precision, calm) applied to this
// product's own content, not a copy of Apple's actual page. The dashboard
// mockup below is the storytelling layer; the copy/nav/CTA above it are
// independent functional UI, each a small reusable component with real
// hover/focus/active/disabled/loading states (see ui/Button.js).

export default function LandingPage({ onGetStarted, onLogin }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [bookDemoOpen, setBookDemoOpen] = useState(false);
  // Pricing section's Monthly/Annual toggle — same 20%-off math as the real
  // in-app plan-selection page (Math.round(price*0.8)), purely a display
  // toggle: every card's CTA still just opens Login, same as before: actual
  // plan choice happens post-login on the real checkout page.
  const [pricingAnnual, setPricingAnnual] = useState(false);
  // The hero photo is kept bright/untouched (no dark overlay), so the nav
  // always uses its original light/frosted appearance — no dark-on-photo
  // variant needed. Kept as a named constant (rather than removing every
  // conditional below) so re-enabling a dark hero later is a one-line change.
  const navOnDark = false;

  // Auto-cycles the hero mockup's sidebar highlight (and its matching KPI
  // tiles) through all six sections every 3.2s, looping back to Dashboard.
  // Hovering the sidebar pauses the cycle (mockupNavPausedRef, read fresh
  // on every tick so pausing/resuming needs no interval restart) and moving
  // the cursor off it resumes from wherever it left off.
  const [mockupNavIndex, setMockupNavIndex] = useState(0);
  const mockupNavPausedRef = useRef(false);
  useEffect(() => {
    const id = setInterval(() => {
      if (!mockupNavPausedRef.current) setMockupNavIndex(i => (i + 1) % MOCKUP_NAV.length);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  // The KPI grid and the Recent-replies card are PERMANENT DOM nodes (refs
  // below) whose content is simply updated in place every cycle — they are
  // never unmounted/remounted. The "flow in" animation is replayed instead
  // by toggling the animation class off and on (with a forced reflow in
  // between) each time mockupNavIndex changes. This intentionally avoids
  // the previous key={mockupNavIndex}-remount approach, which was letting
  // old cycles' KPI/replies cards pile up in the DOM instead of being
  // replaced (reported as "KPI cards multiplying").
  const kpiGridRef = useRef(null);
  const feedCardRef = useRef(null);
  useEffect(() => {
    [[kpiGridRef.current, "lp-kpi-flow"], [feedCardRef.current, "lp-feed-flow"]].forEach(([el, cls]) => {
      if (!el) return;
      el.classList.remove(cls);
      void el.offsetWidth; // force reflow so the re-added class replays the animation
      el.classList.add(cls);
    });
  }, [mockupNavIndex]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    // The team photo is now the background for the WHOLE page (not just
    // the hero) — fixed so it stays put behind Pricing/Footer as the page
    // scrolls, blurred/tinted per-section below so each section's own
    // content stays readable while the photo still shows through.
    <div className="lp-page-bg" style={{ fontFamily: FONT, color: C.text, overflowX: "hidden", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        /* Page-wide background photo. Also applied to html/body (not just
           the wrapper div) with min-height:100% top-to-bottom — on mobile
           Safari/Chrome, the pull-to-refresh/rubber-band overscroll at the
           top and bottom reveals whatever html/body's OWN background is,
           so without this the overscroll bounce showed a plain white gap
           even though the wrapper div's background looked fine otherwise.
           Fixed attachment is desktop-only (iOS ignores/breaks it), and we
           use 100dvh (falls back to 100vh) so mobile's address-bar show/hide
           doesn't leave a short strip uncovered at the bottom either. */
        html, body {
          min-height: 100%;
          min-height: 100dvh;
        }
        html, body, .lp-page-bg {
          background-image: url(${heroPhoto});
          background-size: cover;
          background-position: center 35%;
          background-repeat: no-repeat;
          background-color: ${C.bg};
        }
        html, body { background-attachment: fixed; }
        .lp-page-bg { background-attachment: fixed; min-height: 100vh; min-height: 100dvh; }
        @media (max-width: 768px) {
          html, body, .lp-page-bg { background-attachment: scroll; }
        }

        ${marketingButtonStyles}

        /* Hero headline: manual two-line break only kicks in once there's
           room for it — below that it wraps naturally instead of forcing
           an awkward split, so mobile isn't just a shrunk desktop line. */
        .lp-hero-title { letter-spacing: -1px; }
        .lp-hero-break { display: none; }
        @media (min-width: 560px) { .lp-hero-break { display: inline; } }
        @media (min-width: 640px)  { .lp-hero-title { letter-spacing: -1.4px; } }
        @media (min-width: 1024px) { .lp-hero-title { letter-spacing: -2px; } }

        /* ── Cinematic hero stage ────────────────────────────────────────
           Real photography of an SDR team celebrating results (leads,
           deals, revenue on the shared screen), staged as a full-bleed
           background with a dark scrim behind the headline for contrast —
           the actual "premium enterprise SaaS with a real team" look,
           not an abstract product-only visualization. The photo is set as
           an inline backgroundImage (see JSX) layered under a gradient
           scrim; this class only owns position/overflow. */
        .lp-hero-cinematic {
          position: relative;
          overflow: hidden;
          background-size: cover;
          background-repeat: no-repeat;
        }
        .lp-hero-content { position: relative; z-index: 1; }

        /* Product mockup now fills the entire hero (see inline flex sizing
           on the JSX side) — the old fixed-size "floating card with a
           tilt/lift on hover" treatment no longer fits a full-bleed panel,
           so the tilt transform is retired here. */
        .lp-mockup-frame { transition: box-shadow .5s ease; }

        /* KPI tiles "flow" in from the right each time the auto-cycling
           sidebar switches sections (see mockupNavIndex in the JSX). */
        @keyframes lp-kpi-flow-in {
          from { opacity: 0; transform: translateX(36px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .lp-kpi-flow { animation: lp-kpi-flow-in .55s cubic-bezier(.22,.61,.36,1); }

        /* Recent-replies feed flows the opposite direction — in from the
           left — each time it swaps to a new section's data. */
        @keyframes lp-feed-flow-in {
          from { opacity: 0; transform: translateX(-36px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .lp-feed-flow { animation: lp-feed-flow-in .55s cubic-bezier(.22,.61,.36,1); }

        @media (max-width: 768px) {
          .lp-mockup-grid { grid-template-columns: 1fr !important; }
          .lp-mockup-sidebar { display: none !important; }
          .lp-hero { padding: 112px 6% 56px !important; background-position: center 40% !important; }
        }
        @media (max-width: 480px) {
          .lp-hero { padding: 100px 5% 48px !important; }
          .lp-hero-cta { gap: 16px !important; }
        }

        /* ── Pricing section ─────────────────────────────────────────── */
        .lp-price-segmented { position: relative; display: inline-grid; grid-template-columns: 1fr 1fr; align-items: center; background: #fff; border: 1px solid ${C.borderSoft}; border-radius: 999px; padding: 4px; box-shadow: 0 1px 2px rgba(16,24,40,.04); }
        .lp-price-seg-highlight { position: absolute; top: 4px; bottom: 4px; left: 4px; width: calc(50% - 4px); background: ${C.bgAlt}; border-radius: 999px; box-shadow: inset 0 0 0 1px ${C.borderSoft}; transition: transform .28s cubic-bezier(.32,.72,0,1); z-index: 0; }
        .lp-price-seg-btn { position: relative; z-index: 1; display: inline-flex; align-items: center; gap: 8px; justify-content: center; background: none; border: none; padding: 9px 22px; border-radius: 999px; font-family: ${FONT}; font-size: 13.5px; font-weight: 600; color: ${C.textSub}; cursor: pointer; transition: color .2s ease; white-space: nowrap; }
        .lp-price-seg-btn.is-active { color: ${C.text}; }
        .lp-price-seg-btn:focus-visible { outline: 2px solid ${C.accent}; outline-offset: 2px; }
        .lp-price-save-pill { font-size: 10.5px; font-weight: 700; color: ${C.green}; background: ${C.greenMuted}; padding: 2px 8px; border-radius: 999px; letter-spacing: .2px; }

        .lp-price-card { position: relative; display: flex; flex-direction: column; height: 100%; transition: transform .22s cubic-bezier(.22,.61,.36,1), box-shadow .22s cubic-bezier(.22,.61,.36,1), border-color .22s ease; }
        .lp-price-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(16,24,40,.09) !important; }
        .lp-price-card.is-popular { transform: translateY(-6px); }
        .lp-price-card.is-popular:hover { transform: translateY(-9px); }
        @media (max-width: 1080px) { .lp-price-card.is-popular, .lp-price-card.is-popular:hover { transform: translateY(-3px); } }

        .lp-price-value { display: inline-block; animation: lp-price-morph .32s cubic-bezier(.22,.61,.36,1); }
        @keyframes lp-price-morph { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }

        @media (prefers-reduced-motion: reduce) {
          .lp-price-card, .lp-price-card:hover, .lp-price-card.is-popular, .lp-price-card.is-popular:hover,
          .lp-price-seg-highlight, .lp-price-value { transition: none !important; animation: none !important; transform: none !important; }
          .lp-price-card.is-popular { transform: translateY(-6px) !important; }
        }
      `}</style>

      {/* Mobile nav menu */}
      {isMobile && menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.4)", backdropFilter: "blur(4px)" }}>
          <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 52, left: 0, right: 0, background: "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${C.borderSoft}`, padding: "20px 6% 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <NavLinkButton onClick={() => { setMenuOpen(false); scrollToId("pricing"); }} style={{ opacity: 1, fontSize: 15, padding: "6px 0" }}>Pricing</NavLinkButton>
            <PillButton variant="secondary" onClick={() => { setMenuOpen(false); onLogin(); }}>Log in</PillButton>
            <PillButton variant="primary" onClick={() => { setMenuOpen(false); onGetStarted(); }}>Get started free</PillButton>
          </div>
        </div>
      )}

      {/* NAVBAR — same links/handlers/routes as always; only the color
          scheme adapts, light text while it's still over the new dark
          hero, back to the original light/frosted look the instant real
          (white) content scrolls underneath it. */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: navOnDark && !scrolled ? "rgba(0,0,0,.18)" : "rgba(255,255,255,0.85)", backdropFilter: "blur(16px) saturate(1.6)", WebkitBackdropFilter: "blur(16px) saturate(1.6)", borderBottom: navOnDark && !scrolled ? "1px solid rgba(255,255,255,.08)" : `1px solid ${C.borderSoft}`, padding: "0 5%", height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", transition: "background .25s ease, border-color .25s ease" }}>
        <NavLinkButton onClick={() => { window.location.href = "https://www.sdrreachout.com/"; }} aria-label="SDR ReachOut home" style={{ display: "flex", alignItems: "center", opacity: 1 }}>
          <img src={wordmarkLockup} alt="SDR ReachOut" style={{ height: 48, width: "auto", display: "block" }} />
        </NavLinkButton>
        <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
          {!isMobile && (
            <>
              <NavLinkButton onClick={() => scrollToId("pricing")} style={navOnDark ? { color: "#f5f5f7" } : undefined}>Pricing</NavLinkButton>
              <NavLinkButton onClick={onLogin} style={navOnDark ? { color: "#f5f5f7" } : undefined}>Log in</NavLinkButton>
              {/* Opens the Book a Demo lead-capture form (BookDemoModal) —
                  a separate flow from "Get started free"'s self-serve
                  trial signup. Its final step's own submit button is the
                  one left idle (no real scheduling link yet); everything
                  before that (the steps themselves) works normally. */}
              <PillButton variant="primary" size="sm" onClick={() => setBookDemoOpen(true)}>Book Demo</PillButton>
            </>
          )}
          {isMobile && (
            <button type="button" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} className="pill-btn" style={{ background: "transparent", width: 32, height: 32, padding: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <span style={{ width: 18, height: 1.4, background: navOnDark ? "#fff" : C.text, borderRadius: 1, transition: "transform .2s ease, background .2s ease", transform: menuOpen ? "translateY(3.2px) rotate(45deg)" : "none" }} />
              <span style={{ width: 18, height: 1.4, background: navOnDark ? "#fff" : C.text, borderRadius: 1, transition: "transform .2s ease, opacity .2s ease, background .2s ease", transform: menuOpen ? "translateY(-3.2px) rotate(-45deg)" : "none" }} />
            </button>
          )}
        </div>
      </nav>

      {/* HERO — real photo of the team, kept exactly as uploaded, now with
          one white glass layer covering the FULL hero (edge to edge, not
          just a card around the mockup) — a translucent white wash + blur
          over the entire photo, with the product UI content sitting on top
          of it. Mockup text is near-black (C.text) for readability/brand
          consistency; the KPI/feed cards, active-nav highlight, and avatar
          circle are neutral white/black-tint now too (previously a light
          green tint) — no green left anywhere in the hero mockup. */}
      <section
        className="lp-hero lp-hero-cinematic"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: "88px 6% 32px", textAlign: "center",
          minHeight: "100vh",
        }}
      >
        {/* Full-bleed white glass over the page background photo, scoped to
            the hero section (Pricing/Footer below get their own matching
            glass treatment further down, see those sections). */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,.6)", backdropFilter: "blur(14px) saturate(1.3)", WebkitBackdropFilter: "blur(14px) saturate(1.3)" }} />
        <div className="lp-hero-content" style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", flex: 1, minHeight: 0 }}>
          {/* App preview mockup, now sized to fill the entire hero screen
              (width and height) instead of sitting as a smaller centered
              card — every wrapper below is flex:1 so the card's own
              height tracks the hero section's full height. */}
          <Reveal delay={0.1} style={{ width: "100%", flex: 1, minHeight: 0, display: "flex" }}>
            <div className="lp-mockup-stage" style={{ position: "relative", width: "100%", flex: 1, minHeight: 0, display: "flex" }}>
              <div
                className="lp-mockup-frame"
                style={{
                  width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
                  borderRadius: 20, padding: 10,
                  background: "rgba(255,255,255,.18)",
                  boxShadow: "0 2px 4px rgba(0,0,0,.08), 0 30px 70px rgba(0,0,0,.14)",
                  border: `1px solid ${C.borderSoft}`,
                }}
              >
                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", borderRadius: 13, padding: 22, overflow: "hidden", border: `1px solid ${C.borderSoft}` }}>
                  <div style={{ display: "flex", gap: 7, marginBottom: 18, flexShrink: 0 }}>
                    <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#FF5F56" }} />
                    <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#FFBD2E" }} />
                    <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#27C93F" }} />
                  </div>
                  <div className="lp-mockup-grid" style={{ display: "grid", gridTemplateColumns: "190px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
                    <div
                      className="lp-mockup-sidebar"
                      onMouseEnter={() => { mockupNavPausedRef.current = true; }}
                      onMouseLeave={() => { mockupNavPausedRef.current = false; }}
                      style={{ borderRadius: 10, padding: 12, background: "rgba(255,255,255,.14)", border: `1px solid ${C.borderSoft}` }}
                    >
                      {MOCKUP_NAV.map((item, i) => (
                        <div key={item.label} style={{ padding: "8px 10px", borderRadius: 7, marginBottom: 2, background: i === mockupNavIndex ? "rgba(255,255,255,.6)" : "transparent", color: C.text, opacity: i === mockupNavIndex ? 1 : .82, fontSize: 13, fontWeight: i === mockupNavIndex ? 700 : 500, letterSpacing: "-.1px", transition: "background .4s ease, opacity .4s ease, font-weight .4s ease" }}>{item.label}</div>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", borderRadius: 10, padding: 16, background: "rgba(255,255,255,.14)", border: `1px solid ${C.borderSoft}` }}>
                      <p style={{ fontSize: 17, fontWeight: 700, marginBottom: 16, color: C.text, letterSpacing: "-.2px", flexShrink: 0, transition: "opacity .3s ease" }}>{MOCKUP_NAV[mockupNavIndex].heading}</p>
                      {/* This grid is a single permanent DOM node (ref, no
                          key) — its 3 tiles' text updates in place each
                          cycle, and the .lp-kpi-flow "slide in from the
                          right" animation is replayed via the classList
                          toggle in the useEffect above, instead of
                          unmount/remount (which was letting old cycles'
                          tiles pile up — see the note by kpiGridRef). */}
                      <div ref={kpiGridRef} className="lp-kpi-flow" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 16, flexShrink: 0, overflow: "hidden" }}>
                        {MOCKUP_NAV[mockupNavIndex].kpis.map(([l,v]) => (
                          <div key={l} style={{ borderRadius: 10, padding: 12, background: "rgba(255,255,255,.7)", border: "1px solid rgba(0,0,0,.06)" }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: C.text, opacity: .85, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 4 }}>{l}</p>
                            <p style={{ fontSize: 21, fontWeight: 750, color: C.text, letterSpacing: "-.4px" }}>{v}</p>
                          </div>
                        ))}
                      </div>
                      {/* Same permanent-node + classList-toggle approach as
                          the KPI grid above: one stable card (background,
                          border, label, and rows together) whose content
                          updates in place and whose entrance animation is
                          replayed via the ref-driven useEffect, not by
                          remounting. */}
                      <div ref={feedCardRef} className="lp-feed-flow" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", borderRadius: 10, padding: 12, background: "rgba(255,255,255,.7)", border: "1px solid rgba(0,0,0,.06)", overflow: "hidden" }}>
                        <p style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: C.text, opacity: .85, flexShrink: 0 }}>{MOCKUP_NAV[mockupNavIndex].feedLabel}</p>
                        {MOCKUP_NAV[mockupNavIndex].feed.map(([n,m]) => (
                          <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid rgba(0,0,0,.06)" }}>
                            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(0,0,0,.05)", border: "1px solid rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: C.text, flexShrink: 0 }}>{n[0]}</div>
                            <div><p style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{n}</p><p style={{ fontSize: 11, color: C.text, opacity: .75 }}>{m}</p></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRICING — same plans/prices the real in-app plan-selection &
          checkout flow uses (imported from PlanSelectionPage.js), so this
          marketing section can never silently drift out of sync with what
          a signed-up customer actually sees and pays. Every card's CTA
          starts the same free-trial signup as the hero — actual plan
          selection/payment happens post-signup on the real checkout page,
          same as it does today; this section is purely informational. */}
      <section id="pricing" style={{ padding: "56px 6% 88px" }}>
        {/* Pricing panel — same rounded-glass treatment as the hero's
            mockup card (identical background/border/shadow/radius values),
            rounded on all four corners, floating over the page's photo
            background instead of a full-bleed strip. */}
        <div
          style={{
            maxWidth: 1180, margin: "0 auto", borderRadius: 20, padding: "48px 40px",
            background: "rgba(255,255,255,.6)",
            backdropFilter: "blur(16px) saturate(1.3)", WebkitBackdropFilter: "blur(16px) saturate(1.3)",
            boxShadow: "0 2px 4px rgba(0,0,0,.08), 0 30px 70px rgba(0,0,0,.14)",
            border: `1px solid ${C.borderSoft}`,
          }}
        >
          <Reveal>
            <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 32px" }}>
              <h2 style={{ fontSize: "clamp(26px, 3.2vw, 38px)", fontWeight: 700, letterSpacing: "-1px", marginBottom: 14, color: C.text }}>Simple, transparent pricing</h2>
              <p style={{ fontSize: 16, color: C.textSub, opacity: .95, lineHeight: 1.55 }}>Start free, no credit card required. Upgrade whenever you're ready to scale.</p>
            </div>
          </Reveal>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 36 }}>
            <div className="lp-price-segmented" role="group" aria-label="Billing period">
              <div className="lp-price-seg-highlight" style={{ transform: pricingAnnual ? "translateX(100%)" : "translateX(0%)" }} />
              <button type="button" className={"lp-price-seg-btn" + (!pricingAnnual ? " is-active" : "")} aria-pressed={!pricingAnnual} onClick={() => setPricingAnnual(false)}>
                Monthly
              </button>
              <button type="button" className={"lp-price-seg-btn" + (pricingAnnual ? " is-active" : "")} aria-pressed={pricingAnnual} onClick={() => setPricingAnnual(true)}>
                Annual <span className="lp-price-save-pill">Save 20%</span>
              </button>
            </div>
          </div>

          {/* Cards are a near-solid, blurred white so every price/feature
              line reads cleanly no matter how busy the photo is behind it. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
            {plans.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <div className={"lp-price-card" + (p.popular ? " is-popular" : "")} style={{ background: "rgba(255,255,255,.94)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", borderRadius: 18, padding: "28px 24px", border: p.popular ? `1.5px solid ${C.accent}` : `1px solid ${C.borderSoft}`, boxShadow: p.popular ? "0 4px 10px rgba(0,113,227,.08), 0 22px 44px rgba(0,113,227,.14)" : "0 1px 2px rgba(0,0,0,.04)" }}>
                  {p.badge && (
                    <span style={{ position: "absolute", top: -12, left: 24, display: "inline-flex", alignItems: "center", fontSize: 10.5, fontWeight: 700, letterSpacing: ".4px", padding: "5px 12px", borderRadius: 999, color: "#fff", background: p.popular ? `linear-gradient(120deg, ${C.accent}, ${C.purple})` : C.text, boxShadow: p.popular ? "0 4px 10px rgba(0,113,227,.3)" : "none" }}>{p.badge}</span>
                  )}
                  <p style={{ fontSize: 12, fontWeight: 600, color: C.textFaint, textTransform: "uppercase", letterSpacing: ".5px", marginTop: p.badge ? 10 : 0, marginBottom: 6 }}>{PRICING_POSITIONING[p.id]}</p>
                  <p style={{ fontSize: 16, fontWeight: 650, color: C.text, marginBottom: 6 }}>{p.name}</p>
                  <div style={{ marginBottom: 4 }}>
                    <span className="lp-price-value" key={p.price === 0 ? "free" : (pricingAnnual ? "a" : "m")} style={{ fontSize: 34, fontWeight: 750, color: C.text, letterSpacing: "-.6px" }}>
                      {p.price === 0 ? "Free" : "$" + (pricingAnnual ? Math.round(p.price * 0.8) : p.price)}
                    </span>
                    {p.price > 0 && <span style={{ fontSize: 13, color: C.textSub, opacity: .8 }}>/month</span>}
                  </div>
                  {p.price > 0 && <p style={{ fontSize: 12, color: C.textSub, opacity: .9, marginBottom: 18 }}>{pricingAnnual ? "Billed annually" : "or save 20% billed annually"}</p>}
                  {p.price === 0 && <p style={{ fontSize: 12, color: C.textSub, opacity: .9, marginBottom: 18 }}>{p.leads} leads included, then choose a plan</p>}
                  <div style={{ height: 1, background: C.borderSoft, marginBottom: 18 }} />
                  <ul style={{ listStyle: "none", marginBottom: 22, flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                    {p.features.slice(0, 6).map(f => (
                      <li key={f} style={{ fontSize: 13.5, color: C.textSub, padding: 0, display: "flex", alignItems: "flex-start", gap: 9, lineHeight: 1.4 }}>
                        <PricingCheckIcon tone={p.popular ? C.accent : C.textSub} />{f}
                      </li>
                    ))}
                  </ul>
                  {/* Pricing card CTAs go to Login (not signup) per request —
                      all four plan buttons redirect here now. */}
                  <PillButton variant={p.popular ? "primary" : "secondary"} size="sm" onClick={onLogin} style={{ width: "100%" }}>{p.cta}</PillButton>
                </div>
              </Reveal>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 24, marginTop: 40, flexWrap: "wrap" }}>
            {["No credit card required", "Cancel anytime", "Setup in 2 minutes", "24/7 support"].map(t => (
              <span key={t} style={{ fontSize: 12.5, color: C.textSub }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER — full-bleed to the page's left/right/bottom edges (no
          side/bottom margin, no bottom rounding since it sits flush
          against the end of the page), only rounded on its top corners
          where it meets the Pricing panel above. Same glass opacity/blur
          as Pricing, but text is now pure black per explicit direction. */}
      <footer
        style={{
          width: "100%", padding: "48px 6% 28px",
          background: "rgba(255,255,255,.6)",
          backdropFilter: "blur(16px) saturate(1.3)", WebkitBackdropFilter: "blur(16px) saturate(1.3)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          boxShadow: "0 -2px 4px rgba(0,0,0,.05), 0 -20px 50px rgba(0,0,0,.08)",
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 40, justifyContent: "space-between", marginBottom: 40, maxWidth: 1180, margin: "0 auto 40px" }}>
          <div style={{ maxWidth: 280 }}>
            <button type="button" onClick={() => { window.location.href = "https://www.sdrreachout.com/"; }} aria-label="SDR ReachOut home" style={{ display: "flex", alignItems: "center", marginBottom: 12, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
              <img src={wordmarkLockup} alt="SDR ReachOut" style={{ height: 48, width: "auto", display: "block" }} />
            </button>
            <p style={{ fontSize: 13, color: "#000", lineHeight: 1.6 }}>AI-powered outbound sales — lead discovery, personalized sequences, and meeting booking, in one platform.</p>
          </div>
          <div style={{ display: "flex", gap: 56, flexWrap: "wrap" }}>
            <div>
              <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "#000", marginBottom: 14 }}>Product</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button type="button" onClick={() => scrollToId("pricing")} style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", fontSize: 13.5, color: "#000" }}>Pricing</button>
                <button type="button" onClick={onLogin} style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", fontSize: 13.5, color: "#000" }}>Log in</button>
                {/* Redirects to Login (not signup) per request — matches
                    the pricing card CTAs' behavior. */}
                <button type="button" onClick={onLogin} style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer", fontSize: 13.5, color: "#000" }}>Start free trial</button>
              </div>
            </div>
            <div>
              <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "#000", marginBottom: 14 }}>Legal</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, color: "#000", textDecoration: "none" }}>Terms of Service</a>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, color: "#000", textDecoration: "none" }}>Privacy Policy</a>
              </div>
            </div>
            <div>
              <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "#000", marginBottom: 14 }}>Contact</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ fontSize: 13.5, color: "#000", textDecoration: "none" }}>{CONTACT_EMAIL}</a>
              </div>
            </div>
          </div>
        </div>
        <div style={{ borderTop: `1px solid ${C.borderSoft}`, paddingTop: 22, maxWidth: 1180, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 10 }}>
          <p style={{ fontSize: 12.5, color: "#000" }}>&copy; {new Date().getFullYear()} {LEGAL_ENTITY} All rights reserved.</p>
        </div>
      </footer>

      {bookDemoOpen && <BookDemoModal onClose={() => setBookDemoOpen(false)} />}
    </div>
  );
}
